/**
 * The site page: header (host + connection chip), two tabs — Languages (the
 * site's home) and Settings. There is deliberately no Overview tab: the panel
 * at the top of Languages carries the onboarding checklist / site status.
 *
 * Split out of the old 1500-line site.tsx: the tabs live in ./languages.tsx
 * and ./settings.tsx, the chrome in ./shell.tsx, the shared Cloudflare polling
 * in ./domain-status.ts.
 */

import { useState } from 'react'
import { useLoaderData, useNavigate, useRevalidator, type LoaderFunctionArgs } from 'react-router-dom'

import { requireUser } from '../../auth'
import { trpc } from '../../trpc'

import { domainReady, useDomainStatuses } from './domain-status'
import { LanguagesTab } from './languages'
import { SettingsTab } from './settings'
import { SiteShell, type SiteTab } from './shell'

export async function loader({ params }: LoaderFunctionArgs) {
	await requireUser()
	const { websiteID } = params
	if (!websiteID) {
		throw new Response('Bad request', { status: 400 })
	}

	const website = await trpc().WebsiteService.Get.query(websiteID)
	if (!website) {
		throw new Response('Not found', { status: 404 })
	}

	const { route_list } = await trpc().WebsiteService.ListRoutes.query({
		website_id: website.id,
	})

	return { website, route_list }
}

export function Site() {
	const { website, route_list } = useLoaderData() as Awaited<ReturnType<typeof loader>>
	const navigate = useNavigate()
	const revalidator = useRevalidator()
	const [tab, setTab] = useState<SiteTab>('languages')

	const { statuses, loaded, refresh } = useDomainStatuses(route_list)

	function onChanged() {
		revalidator.revalidate()
	}

	// Until the first status fetch lands, `website.verified` stands in so the
	// chip doesn't flicker between Setting up and Live on every page open.
	const connected = loaded ? domainReady(website, route_list, statuses) : !!website.verified

	return (
		<SiteShell
			website={website}
			connected={connected}
			tab={tab}
			onTab={setTab}
			onBack={() => navigate('/')}
		>
			{tab === 'languages' ? (
				<LanguagesTab
					website={website}
					routes={route_list}
					statuses={statuses}
					refresh={refresh}
					onChanged={onChanged}
				/>
			) : (
				<SettingsTab
					website={website}
					routes={route_list}
					onChanged={onChanged}
				/>
			)}
		</SiteShell>
	)
}
